<template>
  <ModalComponent
    v-if="isVisible"
    :submit-button-text="isEditMode ? 'Save' : 'Create'"
    :submit="handleSubmit"
    :submit-disabled="!isValid || isProcessing"
    size="lg"
    @close="handleClose"
    @set-force-toggle="handleSetForceToggle"
  >
    <template #title>
      {{ isEditMode ? "Edit Fee Item" : "Add Fee Item" }}
    </template>
    <template #body>
      <div class="mb-3">
        <label class="form-label">Type</label>
        <select v-model="form.item_type" class="form-select">
          <option value="" disabled>Select fee type</option>
          <option v-for="t in feeItemTypes" :key="t.value" :value="t.value">
            {{ t.label }}
          </option>
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label">Description</label>
        <input
          v-model="form.description"
          type="text"
          class="form-control"
          placeholder="e.g. Registry import fee (LV)"
        />
      </div>
      <div class="row">
        <div class="col-6 mb-3">
          <label class="form-label">Amount (MWh)</label>
          <input
            v-model.number="form.amount"
            type="number"
            class="form-control"
            min="1"
            step="1"
          />
        </div>
        <div class="col-6 mb-3">
          <label class="form-label">Unit Price (EUR/MWh)</label>
          <input
            v-model.number="form.unit_price"
            type="number"
            class="form-control"
            min="0.0001"
            step="0.0001"
          />
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { InvoiceItem } from "@/types";
import { computed, ref, watch } from "vue";

const FEE_ITEM_TYPES = [
  "FEE_SOLDERA_COMMISSION",
  "FEE_SOLDERA_PROCUREMENT",
  "FEE_SOLDERA_SERVICE",
  "FEE_SOLDERA_ISSUANCE",
  "FEE_SOLDERA_CANCELLATION",
  "FEE_SOLDERA_TRANSFER",
  "FEE_REGISTRY_EXPORT",
  "FEE_REGISTRY_IMPORT",
  "FEE_REGISTRY_CANCELLATION",
  "FEE_REGISTRY_ISSUANCE",
  "FEE_GO_PURCHASE",
] as const;

const feeItemTypes = FEE_ITEM_TYPES.map((v) => ({
  value: v,
  label: v,
}));

const props = defineProps<{
  isVisible: boolean;
  companyId: number;
  companyCountry?: string;
  editItem?: InvoiceItem | null;
}>();

const emit = defineEmits<{
  saved: [];
  close: [];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const isProcessing = ref(false);
const feeTypeDescriptions = ref<Record<string, string>>({});

const form = ref({
  item_type: "",
  description: "",
  amount: 0,
  unit_price: 0,
});

const isEditMode = computed(() => !!props.editItem);

const isValid = computed(() => {
  return (
    form.value.item_type !== "" &&
    form.value.description.trim() !== "" &&
    form.value.amount > 0 &&
    form.value.unit_price > 0
  );
});

watch(
  () => props.isVisible,
  (visible) => {
    if (visible && props.editItem) {
      form.value = {
        item_type: props.editItem.item_type,
        description: props.editItem.description,
        amount: props.editItem.amount,
        unit_price: props.editItem.unit_price,
      };
    } else if (visible) {
      form.value = { item_type: "", description: "", amount: 0, unit_price: 0 };
    }
  }
);

watch(
  () => props.companyCountry,
  async (country) => {
    feeTypeDescriptions.value = {};
    if (!country) return;
    try {
      const response = await goSolidApi.get(
        `/api/admin/fee-item-descriptions/${country}`
      );
      if (response.status === 200) {
        feeTypeDescriptions.value = response.data;
      }
    } catch (error) {
      globalErrorHandler(error);
    }
  },
  { immediate: true }
);

watch(
  () => form.value.item_type,
  (newType) => {
    if (isEditMode.value) return;
    const defaultDesc = feeTypeDescriptions.value[newType];
    if (defaultDesc) {
      form.value.description = defaultDesc;
    }
  }
);

function handleSetForceToggle(fn: (value: boolean) => void) {
  fn(true);
}

function handleClose() {
  emit("close");
}

async function handleSubmit() {
  if (!isValid.value) return;

  isProcessing.value = true;
  try {
    if (isEditMode.value && props.editItem) {
      await goSolidApi.put(
        `/api/admin/company/${props.companyId}/invoicing/fee-items/${props.editItem.id}`,
        form.value
      );
      globalSuccessHandler("Success", "Fee item updated");
    } else {
      await goSolidApi.post(
        `/api/admin/company/${props.companyId}/invoicing/fee-items`,
        form.value
      );
      globalSuccessHandler("Success", "Fee item created");
    }
    emit("saved");
    handleClose();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}
</script>
